// Gráfico de tendência único, no estilo dos supervisórios: todas as penas no mesmo gráfico,
// cada uma com a SUA escala (ocupa a altura toda), e o eixo vertical mostrando a escala da
// pena ativa — na cor dela. Clicar numa pena da tabela inferior troca o eixo. O cursor dá o
// valor real de todas.
//
// Zoom por área (com `areaZoom` ligado — botão na tela): arrastar sobre o gráfico chama
// `onZoom(de, até)`; desligado, arrastar não faz nada. Quem controla a janela
// é a tela, que busca os dados de novo (e com resolução mais fina, se couber). Duplo clique
// chama `onReset`.
//
// Cursores de medição A/B: ligados por padrão (desligar fica lembrado no navegador), são
// duas linhas verticais que se arrastam livremente no tempo. A tabela mostra o valor de cada
// pena em A e em B (a leitura mais próxima), o delta B − A e o Δt entre os dois.
//
// Marcas: linha de 2 px; nas resoluções agregadas, a faixa mínimo–máximo em ~10% de opacidade.

import { Crosshair, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { isDark, token } from "../lib/palette";
import { type Display, formatValue } from "../lib/units";
import { cx } from "./ui";

export interface Pen {
  tag: string;
  description: string;
  color: string;
  display: Display;
  /** [ms, média, mín, máx] */
  points: [number, number, number, number][];
}

interface Marks {
  a: number | null;
  b: number | null;
}

/** Faixa automática da pena, com folga; linha reta ganha uma faixa em volta do valor. */
function penRange(_u: uPlot, min: number | null, max: number | null): uPlot.Range.MinMax {
  if (min === null || max === null) return [0, 1];
  if (min === max) {
    const pad = Math.abs(min) * 0.01 || 1;
    return [min - pad, max + pad];
  }
  const pad = (max - min) * 0.08;
  return [min - pad, max + pad];
}

/** Valor médio (SI) da leitura mais próxima de `t` — busca binária, os pontos vêm em ordem. */
function valueAt(points: Pen["points"], t: number): number | null {
  if (!points.length) return null;
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid]![0] <= t) lo = mid;
    else hi = mid;
  }
  const best = Math.abs(points[lo]![0] - t) <= Math.abs(points[hi]![0] - t) ? lo : hi;
  return points[best]![1];
}

/** 12,4 s · 5 min 03 s · 2 h 15 min · 3 d 04 h */
export function formatDuration(ms: number): string {
  const s = Math.abs(ms) / 1000;
  if (s < 60) return `${s.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} s`;
  const p = (n: number) => String(Math.floor(n)).padStart(2, "0");
  if (s < 3600) return `${Math.floor(s / 60)} min ${p(s % 60)} s`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ${p((s % 3600) / 60)} min`;
  return `${Math.floor(s / 86400)} d ${p((s % 86400) / 3600)} h`;
}

const fmtTime = (ms: number) => new Date(ms).toLocaleString("pt-BR");

const MEASURE_KEY = "insight.trend.cursores";

export function TrendChart({
  pens,
  xRange,
  aggregated,
  activeTag,
  onActivate,
  onRemove,
  onZoom,
  onReset,
  addSlot,
  plotHeight = "420px",
  areaZoom = true,
}: {
  pens: Pen[];
  xRange: [number, number];
  aggregated: boolean;
  activeTag: string | null;
  onActivate: (tag: string) => void;
  onRemove: (tag: string) => void;
  onZoom: (from: number, to: number) => void;
  onReset: () => void;
  /** Botão/controle de adicionar pena, abaixo da tabela. */
  addSlot?: ReactNode;
  /** Altura da área de plotagem (CSS); a tabela de penas fica abaixo, fora dela. */
  plotHeight?: string;
  /** Arrastar sobre o gráfico seleciona o trecho a ampliar; desligado, arrastar não faz nada. */
  areaZoom?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);
  const [themeTick, setThemeTick] = useState(0);
  // Ligados por padrão; quem desliga fica desligado (preferência deste navegador).
  const [measure, setMeasure] = useState(() => {
    try {
      return localStorage.getItem(MEASURE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const [marks, setMarks] = useState<Marks>({ a: null, b: null });
  // O uPlot guarda as funções do momento da criação; estes refs dão a ele o estado atual.
  const cb = useRef({ onZoom, onReset });
  cb.current = { onZoom, onReset };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const xRangeRef = useRef(xRange);
  xRangeRef.current = xRange;
  const areaZoomRef = useRef(areaZoom);
  areaZoomRef.current = areaZoom;
  const marksRef = useRef(marks);
  marksRef.current = marks;

  const active = Math.max(0, pens.findIndex((p) => p.tag === activeTag));
  const stride = aggregated ? 3 : 1;

  // Dados alinhados no eixo X (uPlot exige um X comum a todas as séries).
  const data = useMemo(() => {
    const tables = pens.map((s) => {
      const xs = s.points.map((p) => p[0] / 1000);
      const cols: (number | null)[][] = [s.points.map((p) => p[1] / s.display.factor)];
      if (aggregated) {
        cols.push(s.points.map((p) => p[2] / s.display.factor));
        cols.push(s.points.map((p) => p[3] / s.display.factor));
      }
      return [xs, ...cols] as uPlot.AlignedData;
    });
    return tables.length ? uPlot.join(tables) : ([[]] as unknown as uPlot.AlignedData);
  }, [pens, aggregated]);

  // Mínimo, média e máximo da janela (SI). Agregado: mín dos mínimos e máx dos máximos,
  // senão um pico dentro de um balde de 1 min sumiria na média.
  const stats = useMemo(
    () =>
      pens.map((s) => {
        if (!s.points.length) return null;
        let min = Infinity;
        let max = -Infinity;
        let sum = 0;
        for (const p of s.points) {
          if (p[2] < min) min = p[2];
          if (p[3] > max) max = p[3];
          sum += p[1];
        }
        return { min, max, avg: sum / s.points.length };
      }),
    [pens],
  );

  // O canvas não lê CSS: ao trocar o tema (navegador ou forçado pelo grupo), redesenha.
  useEffect(() => {
    const on = () => setThemeTick((t) => t + 1);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", on);
    const mo = new MutationObserver(on);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      mq.removeEventListener("change", on);
      mo.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const muted = token("--muted");
    const fg = token("--fg");
    const surface = token("--surface");
    const grid = isDark() ? "#2b3441" : "#e8ebef";
    const ap = pens[active];

    // Lê o ref: a janela muda (ao vivo, zoom) sem recriar o gráfico.
    const scales: uPlot.Scales = { x: { time: true, range: () => [xRangeRef.current[0] / 1000, xRangeRef.current[1] / 1000] } };
    const uSeries: uPlot.Series[] = [{}];
    const bands: uPlot.Band[] = [];
    pens.forEach((s, i) => {
      const scale = `p${i}`;
      scales[scale] = { auto: true, range: penRange };
      const main = uSeries.length;
      // A pena ativa um pouco mais grossa.
      uSeries.push({ label: s.description, scale, stroke: s.color, width: i === active ? 2.5 : 1.75, points: { show: false } });
      if (aggregated) {
        uSeries.push({ label: `${s.description} (mín)`, scale, stroke: "transparent", points: { show: false } });
        uSeries.push({ label: `${s.description} (máx)`, scale, stroke: "transparent", points: { show: false } });
        bands.push({ series: [main + 2, main + 1], fill: `${s.color}1a` });
      }
    });

    // Cursores A/B: linha tracejada na cor do texto, com a letra no topo (identidade por rótulo).
    const drawMarks = (u: uPlot) => {
      const { a, b } = marksRef.current;
      const ctx = u.ctx;
      const top = u.bbox.top;
      const bottom = u.bbox.top + u.bbox.height;
      const pr = uPlot.pxRatio;
      ctx.save();
      for (const [label, t] of [["A", a], ["B", b]] as const) {
        if (t === null) continue;
        const x = Math.round(u.valToPos(t / 1000, "x", true));
        if (x < u.bbox.left || x > u.bbox.left + u.bbox.width) continue;
        ctx.strokeStyle = fg;
        ctx.lineWidth = 1.5 * pr;
        ctx.setLineDash([5 * pr, 4 * pr]);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        const w = 16 * pr;
        ctx.fillStyle = fg;
        ctx.fillRect(x - w / 2, top, w, w);
        ctx.fillStyle = surface;
        ctx.font = `bold ${11 * pr}px Inter, system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, x, top + w / 2 + pr);
      }
      ctx.restore();
    };

    const size = () => ({ width: el.clientWidth, height: Math.max(260, el.clientHeight) });
    const opts: uPlot.Options = {
      ...size(),
      legend: { show: false },
      scales,
      series: uSeries,
      bands,
      cursor: {
        points: { size: 8, width: 2, stroke: surface },
        // Zoom por área só no tempo; quem aplica é a tela (busca de novo, resolução certa).
        drag: { x: true, y: false, setScale: false },
      },
      select: { show: true, left: 0, top: 0, width: 0, height: 0 },
      axes: [
        {
          stroke: muted,
          grid: { stroke: grid, width: 1 },
          ticks: { stroke: grid, width: 1 },
          font: "11px Inter, system-ui, sans-serif",
          space: 80,
          // 24 h, e a data junto quando a janela passa de um dia (o padrão do uPlot é americano).
          values: (_u, splits) => {
            const multiDay = xRangeRef.current[1] - xRangeRef.current[0] > 86400_000;
            return splits.map((s) => {
              const d = new Date(s * 1000);
              const hm = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
              return multiDay ? `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}\n${hm}` : hm;
            });
          },
        },
        {
          // Só o eixo da pena ativa, na cor dela: é a escala que o operador está lendo.
          scale: ap ? `p${active}` : undefined,
          stroke: ap?.color ?? muted,
          grid: { stroke: grid, width: 1 },
          ticks: { show: false },
          size: 72,
          font: "11px Inter, system-ui, sans-serif",
          values: (_u, vals) =>
            vals.map((v) => (ap ? formatValue(v * ap.display.factor, { ...ap.display, decimals: Math.min(ap.display.decimals, 2) }) : String(v))),
        },
      ],
      hooks: {
        setCursor: [(u) => setCursorIdx(u.cursor.idx ?? null)],
        setSelect: [
          (u) => {
            if (u.select.width < 5) return;
            const from = u.posToVal(u.select.left, "x") * 1000;
            const to = u.posToVal(u.select.left + u.select.width, "x") * 1000;
            u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
            // Menos de 10 s não tem o que mostrar (as leituras são de 2 s).
            if (to - from >= 10_000) cb.current.onZoom(Math.round(from), Math.round(to));
          },
        ],
        draw: [drawMarks],
      },
    };
    const u = new uPlot(opts, data, el);
    plot.current = u;
    const dbl = () => cb.current.onReset();
    // Cursores A/B: pegar a linha (±8 px) e arrastar livremente no tempo. O mousedown é
    // capturado no root, antes de chegar ao `over`, para o uPlot não começar o zoom por área.
    let dragging: keyof Marks | null = null;
    const hit = (clientX: number): keyof Marks | null => {
      if (!measureRef.current) return null;
      const x = clientX - u.over.getBoundingClientRect().left;
      let best: keyof Marks | null = null;
      let dist = 8;
      for (const k of ["a", "b"] as const) {
        const t = marksRef.current[k];
        if (t === null) continue;
        const d = Math.abs(u.valToPos(t / 1000, "x") - x);
        if (d < dist) {
          dist = d;
          best = k;
        }
      }
      return best;
    };
    const move = (e: MouseEvent) => {
      if (!dragging) return;
      const r = u.over.getBoundingClientRect();
      const x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
      const t = u.posToVal(x, "x") * 1000;
      const k = dragging;
      setMarks((m) => ({ ...m, [k]: t }));
    };
    const up = () => {
      dragging = null;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    const down = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const k = hit(e.clientX);
      if (!k) {
        // Fora dos cursores: sem o modo de zoom por área, o uPlot nem fica sabendo do
        // mousedown — não desenha seleção nem amplia.
        if (!areaZoomRef.current) e.stopPropagation();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      dragging = k;
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
    };
    // Sobre a linha, o ponteiro vira "arrastar para os lados"; no zoom por área, mira.
    const hover = (e: MouseEvent) => {
      if (!dragging) u.over.style.cursor = hit(e.clientX) ? "ew-resize" : areaZoomRef.current ? "zoom-in" : "";
    };
    u.over.addEventListener("dblclick", dbl);
    u.root.addEventListener("mousedown", down, true);
    u.over.addEventListener("mousemove", hover);
    const ro = new ResizeObserver(() => u.setSize(size()));
    ro.observe(el);
    return () => {
      ro.disconnect();
      up();
      u.over.removeEventListener("dblclick", dbl);
      u.root.removeEventListener("mousedown", down, true);
      u.over.removeEventListener("mousemove", hover);
      u.destroy();
      plot.current = null;
    };
    // Recria ao mudar a estrutura (penas, pena ativa, agregação, tema); dados novos: setData.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pens.map((s) => `${s.tag}:${s.color}:${s.display.unit}:${s.display.factor}`).join("|"), active, aggregated, themeTick]);

  useEffect(() => {
    const u = plot.current;
    if (!u) return;
    u.setData(data, true);
    u.setScale("x", { min: xRange[0] / 1000, max: xRange[1] / 1000 });
  }, [data, xRange]);

  // Cursores ligados: começam em 25% e 75% da janela, e voltam para lá quando a janela
  // (zoom, calendário, ao vivo) deixa algum deles de fora.
  useEffect(() => {
    if (!measure) return;
    const [f, t] = xRange;
    const inside = (v: number | null) => v !== null && v >= f && v <= t;
    if (!inside(marks.a) || !inside(marks.b)) setMarks({ a: f + (t - f) * 0.25, b: f + (t - f) * 0.75 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, xRange[0], xRange[1]]);

  // Cursores mudaram: só redesenha (o gancho `draw` lê o ref).
  useEffect(() => {
    plot.current?.redraw(false, false);
  }, [marks]);

  const xs = data[0] as number[];
  const idx = cursorIdx ?? (xs.length ? xs.length - 1 : null);
  const at = idx !== null && xs[idx] !== undefined ? new Date(xs[idx]! * 1000) : null;
  const showMarks = measure && marks.a !== null && marks.b !== null;
  const dt = marks.a !== null && marks.b !== null ? marks.b - marks.a : null;

  function toggleMeasure() {
    const on = !measure;
    if (!on) setMarks({ a: null, b: null });
    setMeasure(on);
    try {
      localStorage.setItem(MEASURE_KEY, on ? "1" : "0");
    } catch {
      /* navegação privada: só não lembra */
    }
  }

  return (
    <div className="flex flex-col rounded-lg border border-line bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 pt-2">
        <span className="text-xs text-muted">
          {pens[active] ? (
            <>
              Escala: <span style={{ color: pens[active]!.color }}>■</span> {pens[active]!.description} ({pens[active]!.display.unit})
            </>
          ) : null}
        </span>
        <span className="flex flex-wrap items-center gap-3 text-xs text-muted tabular-nums">
          {showMarks && (
            <span>
              A {fmtTime(marks.a!)} · B {fmtTime(marks.b!)} · <span className="font-medium text-fg">Δt {dt! < 0 ? "−" : ""}{formatDuration(dt!)}</span>
            </span>
          )}
          <span>{at ? `${cursorIdx === null ? "último " : ""}${at.toLocaleString("pt-BR")}` : "sem dados na janela"}</span>
          <button
            type="button"
            onClick={toggleMeasure}
            aria-pressed={measure}
            className={cx(
              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5",
              measure ? "border-brand bg-brand text-white" : "border-line text-fg hover:bg-surface-2",
            )}
            title="Dois cursores para medir a diferença entre dois instantes"
          >
            <Crosshair className="size-3" aria-hidden /> Cursores A/B
          </button>
        </span>
      </div>
      <div ref={box} style={{ height: plotHeight }} className="w-full px-1" />
      {/* Penas: identidade nunca só pela cor (nome + unidade). Mín/méd/máx são da janela
          inteira; o valor do cursor acompanha o mouse. Clicar na linha troca a escala. */}
      <div className="overflow-x-auto border-t border-line">
        <table className="w-full text-xs">
          <thead className="text-muted">
            <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-1 [&>th]:font-normal">
              <th className="text-left">Pena</th>
              <th className="text-right">{cursorIdx === null ? "Último" : "Cursor"}</th>
              <th className="text-right">Mínimo</th>
              <th className="text-right">Média</th>
              <th className="text-right">Máximo</th>
              {showMarks && (
                <>
                  <th className="border-l border-line text-right">A</th>
                  <th className="text-right">B</th>
                  <th className="text-right">Δ (B − A)</th>
                </>
              )}
              <th className="text-left">Unid.</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pens.map((s, i) => {
              const col = data[1 + i * stride] as (number | null)[] | undefined;
              const v = idx !== null && col ? col[idx] : null;
              const st = stats[i];
              const on = i === active;
              const f = (x: number | null | undefined) => (x === null || x === undefined ? "—" : formatValue(x, s.display));
              const va = marks.a !== null ? valueAt(s.points, marks.a) : null;
              const vb = marks.b !== null ? valueAt(s.points, marks.b) : null;
              const delta = va !== null && vb !== null ? vb - va : null;
              return (
                <tr
                  key={s.tag}
                  onClick={() => onActivate(s.tag)}
                  className={cx("cursor-pointer border-t border-line [&>td]:whitespace-nowrap [&>td]:px-2 [&>td]:py-1", on ? "bg-surface-2" : "hover:bg-surface-2")}
                  title="Mostrar a escala desta pena"
                  aria-selected={on}
                >
                  <td className="text-left">
                    <span className="flex items-center gap-2">
                      <span className={cx("w-4 shrink-0 rounded-full", on ? "h-1" : "h-0.5")} style={{ background: s.color }} aria-hidden />
                      <span className={on ? "font-semibold" : undefined}>{s.description}</span>
                      {on && <span className="text-muted">(escala)</span>}
                    </span>
                  </td>
                  <td className="text-right font-medium tabular-nums">{v === null || v === undefined ? "—" : formatValue(v * s.display.factor, s.display)}</td>
                  <td className="text-right tabular-nums">{f(st?.min)}</td>
                  <td className="text-right tabular-nums">{f(st?.avg)}</td>
                  <td className="text-right tabular-nums">{f(st?.max)}</td>
                  {showMarks && (
                    <>
                      <td className="border-l border-line text-right tabular-nums">{f(va)}</td>
                      <td className="text-right tabular-nums">{f(vb)}</td>
                      <td className="text-right font-medium tabular-nums">
                        {delta === null ? "—" : `${delta > 0 ? "+" : ""}${formatValue(delta, s.display)}`}
                      </td>
                    </>
                  )}
                  <td className="text-left text-muted">{s.display.unit}</td>
                  <td className="w-6 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(s.tag);
                      }}
                      className="rounded p-0.5 text-muted hover:text-fg"
                      aria-label={`Tirar ${s.description}`}
                    >
                      <X className="size-3" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {addSlot && <div className="border-t border-line px-2 py-1.5">{addSlot}</div>}
      </div>
    </div>
  );
}
